import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/env';
import '../styles/farmacia-premium.css';

function plStatusKey(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'entregado') return 'entregado';
  if (e === 'cancelado') return 'cancelado';
  return 'activo';
}

function plEventTone(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'entregado') return 'success';
  if (e === 'cancelado') return 'danger';
  if (e === 'en_camino') return 'emit';
  if (e === 'en_preparacion') return 'assign';
  return 'create';
}

function plEventIcon(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'entregado') return '✓';
  if (e === 'cancelado') return '✕';
  if (e === 'en_camino') return '🚚';
  if (e === 'en_preparacion') return '🧪';
  if (e === 'pedido_generado_farmacia') return '💊';
  return '●';
}

const PERIOD_PRESETS = [
  { id: '7', label: '7 días', days: 7 },
  { id: '30', label: '30 días', days: 30 },
  { id: '90', label: '90 días', days: 90 }
];

const ESTADO_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pendiente_call_center', label: 'Sin gestión' },
  { value: 'pedido_generado_farmacia', label: 'En farmacia' },
  { value: 'en_preparacion', label: 'En preparación' },
  { value: 'en_camino', label: 'En camino' },
  { value: 'entregado', label: 'Entregado' },
  { value: 'cancelado', label: 'Cancelado' }
];

function getDefaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10)
  };
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDateTimeCompact(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const withYear = d.getFullYear() !== now.getFullYear();
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: '2-digit' } : {}),
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDni(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '—';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function estadoLabel(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'pendiente_call_center') return 'Sin gestión';
  if (e === 'pedido_generado_farmacia') return 'En farmacia';
  if (e === 'en_preparacion') return 'En preparación';
  if (e === 'en_camino') return 'En camino';
  if (e === 'entregado') return 'Entregado';
  if (e === 'cancelado') return 'Cancelado';
  return estado || '—';
}

function estadoClass(estado) {
  const e = String(estado || '').toLowerCase().replace(/[^a-z0-9_]/g, '');
  return e || 'default';
}

function operatorInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function estadoLabelShort(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'pendiente_call_center') return 'Sin gest.';
  if (e === 'pedido_generado_farmacia') return 'Farmacia';
  if (e === 'en_preparacion') return 'Prep.';
  if (e === 'en_camino') return 'Camino';
  if (e === 'entregado') return 'Entregado';
  if (e === 'cancelado') return 'Cancelado';
  return estadoLabel(estado);
}

function EstadoPill({ estado, compact = false, micro = false, short = false }) {
  const label = short ? estadoLabelShort(estado) : estadoLabel(estado);
  return (
    <span
      className={`crm-pl-estado-pill${compact ? ' crm-pl-estado-pill--compact' : ''}${micro ? ' crm-pl-estado-pill--micro' : ''} is-${estadoClass(estado)}`}
      title={estadoLabel(estado)}
    >
      {label}
    </span>
  );
}

function EstadoTransition({ previousEstado, newEstado, micro = true }) {
  return (
    <div className={`crm-pl-transition${micro ? ' crm-pl-transition--micro' : ''}`}>
      {previousEstado ? (
        <EstadoPill estado={previousEstado} micro={micro} short />
      ) : (
        <span className="crm-pl-transition__inicio">Inicio</span>
      )}
      <span className="crm-pl-transition__arrow" aria-hidden>→</span>
      <EstadoPill estado={newEstado} micro={micro} short />
    </div>
  );
}

function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z"
        stroke="currentColor"
        strokeWidth="1.85"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.75" stroke="currentColor" strokeWidth="1.85" />
    </svg>
  );
}

function DetailEyeButton({ pedidoId, onClick, className = '' }) {
  return (
    <button
      type="button"
      className={`crm-pl-icon-btn${className ? ` ${className}` : ''}`}
      onClick={onClick}
      aria-label={`Ver registro del pedido ${pedidoId}`}
      title="Ver registro"
    >
      <EyeIcon />
    </button>
  );
}

function LedgerLoading({ message = 'Cargando…' }) {
  return (
    <div className="crm-pl-loading" role="status" aria-live="polite" aria-busy="true">
      <div className="crm-gd-spinner" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

export default function PulseLedgerPanel() {
  const defaults = useMemo(() => getDefaultRange(), []);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [estado, setEstado] = useState('');
  const [clienteDni, setClienteDni] = useState('');
  const [changedBy, setChangedBy] = useState('');
  const [pedidoId, setPedidoId] = useState('');
  const [activePreset, setActivePreset] = useState('7');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState(null);
  const [detailPedido, setDetailPedido] = useState(null);
  const [detailItems, setDetailItems] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sideCollapsed, setSideCollapsed] = useState(false);
  const [opsOpen, setOpsOpen] = useState(false);
  const [opsFilter, setOpsFilter] = useState(null);
  const [opsPos, setOpsPos] = useState({ top: 0, left: 0 });
  const opsBtnRef = useRef(null);
  const [pedSearch, setPedSearch] = useState('');
  const [pedStatus, setPedStatus] = useState('all');
  const [pedPageSize, setPedPageSize] = useState(6);
  const [pedPage, setPedPage] = useState(1);
  const [logSearch, setLogSearch] = useState('');
  const [logPageSize, setLogPageSize] = useState(10);
  const [logPage, setLogPage] = useState(1);
  const [openLogId, setOpenLogId] = useState(null);

  const applyPreset = useCallback((days, presetId) => {
    if (days == null || !Number.isFinite(Number(days))) {
      setActivePreset('');
      return;
    }
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - Number(days));
    setFrom(start.toISOString().slice(0, 10));
    setTo(end.toISOString().slice(0, 10));
    setActivePreset(String(presetId || ''));
  }, []);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ from, to, limit: '150', pedidos_limit: '80' });
      if (estado) params.set('estado', estado);
      if (clienteDni.trim()) params.set('cliente_dni', clienteDni.replace(/\D/g, ''));
      if (changedBy.trim()) params.set('changed_by', changedBy.trim());
      if (pedidoId.trim()) params.set('pedido_id', pedidoId.trim());

      const res = await fetch(
        `${API_BASE_URL}/api/asociacion-recetas/pedidos-callcenter/trazabilidad?${params}`,
        { credentials: 'include' }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || json?.message || `Error ${res.status}`);
      setAnalytics(json?.data || null);
    } catch (err) {
      setAnalytics(null);
      setError(err?.message || 'No se pudo cargar la trazabilidad');
    } finally {
      setLoading(false);
    }
  }, [from, to, estado, clienteDni, changedBy, pedidoId]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const totals = analytics?.totals || {};
  const operators = analytics?.operators || [];
  const trace = analytics?.trace || [];
  const pedidos = analytics?.pedidos || [];

  const groupedTrace = useMemo(() => {
    const map = new Map();
    const order = [];
    for (const row of trace) {
      const id = row?.pedido_cab_id;
      if (id == null) continue;
      if (!map.has(id)) {
        map.set(id, {
          pedidoId: id,
          paciente: row.paciente,
          cliente_dni: row.cliente_dni,
          nro_pedido_farmacia: row.nro_pedido_farmacia,
          estadoActual: row.pedido_estado_actual,
          events: []
        });
        order.push(id);
      }
      map.get(id).events.push(row);
    }
    return order.map((id) => {
      const g = map.get(id);
      return { ...g, statusKey: plStatusKey(g.estadoActual) };
    });
  }, [trace]);

  const opsFilterIds = useMemo(() => {
    if (!opsFilter) return null;
    const set = new Set();
    for (const row of trace) {
      if (row?.pedido_cab_id != null && String(row.changed_by || '') === opsFilter) {
        set.add(row.pedido_cab_id);
      }
    }
    return set;
  }, [trace, opsFilter]);

  const logFiltered = useMemo(() => {
    const base = opsFilterIds ? groupedTrace.filter((g) => opsFilterIds.has(g.pedidoId)) : groupedTrace;
    const q = logSearch.trim().toLowerCase();
    if (!q) return base;
    const qPlain = q.replace(/^#/, '');
    const qDigits = q.replace(/\D/g, '');
    return base.filter((g) => {
      const idStr = String(g.pedidoId);
      const name = String(g.paciente || '').toLowerCase();
      const dni = String(g.cliente_dni || '').replace(/\D/g, '');
      const ref = String(g.nro_pedido_farmacia || '').toLowerCase();
      return (
        idStr.includes(qPlain)
        || name.includes(q)
        || ref.includes(q)
        || (qDigits.length > 0 && dni.includes(qDigits))
      );
    });
  }, [groupedTrace, logSearch, opsFilterIds]);

  const logTotal = logFiltered.length;
  const logTotalPages = logPageSize <= 0 ? 1 : Math.max(1, Math.ceil(logTotal / logPageSize));
  const logPageItems = useMemo(() => {
    if (logPageSize <= 0) return logFiltered;
    const start = (logPage - 1) * logPageSize;
    return logFiltered.slice(start, start + logPageSize);
  }, [logFiltered, logPage, logPageSize]);
  const logRangeStart = logTotal === 0 ? 0 : (logPageSize <= 0 ? 1 : (logPage - 1) * logPageSize + 1);
  const logRangeEnd = logPageSize <= 0 ? logTotal : Math.min(logPage * logPageSize, logTotal);

  const pedSearchFiltered = useMemo(() => {
    const base = opsFilterIds ? pedidos.filter((p) => opsFilterIds.has(p.id)) : pedidos;
    const q = pedSearch.trim().toLowerCase();
    if (!q) return base;
    const qPlain = q.replace(/^#/, '');
    const qDigits = q.replace(/\D/g, '');
    return base.filter((p) => {
      const idStr = String(p.id);
      const name = String(p.paciente || '').toLowerCase();
      const dni = String(p.cliente_dni || '').replace(/\D/g, '');
      const ref = String(p.nro_pedido_farmacia || '').toLowerCase();
      return (
        idStr.includes(qPlain)
        || name.includes(q)
        || ref.includes(q)
        || (qDigits.length > 0 && dni.includes(qDigits))
      );
    });
  }, [pedidos, pedSearch, opsFilterIds]);

  const pedStatusCounts = useMemo(() => {
    const acc = { all: pedSearchFiltered.length, activo: 0, entregado: 0, cancelado: 0 };
    for (const p of pedSearchFiltered) {
      const key = plStatusKey(p.estado);
      if (key in acc) acc[key] += 1;
    }
    return acc;
  }, [pedSearchFiltered]);

  const pedFiltered = useMemo(() => {
    if (pedStatus === 'all') return pedSearchFiltered;
    return pedSearchFiltered.filter((p) => plStatusKey(p.estado) === pedStatus);
  }, [pedSearchFiltered, pedStatus]);

  const pedTotal = pedFiltered.length;
  const pedTotalPages = pedPageSize <= 0 ? 1 : Math.max(1, Math.ceil(pedTotal / pedPageSize));
  const pedPageItems = useMemo(() => {
    if (pedPageSize <= 0) return pedFiltered;
    const start = (pedPage - 1) * pedPageSize;
    return pedFiltered.slice(start, start + pedPageSize);
  }, [pedFiltered, pedPage, pedPageSize]);
  const pedRangeStart = pedTotal === 0 ? 0 : (pedPageSize <= 0 ? 1 : (pedPage - 1) * pedPageSize + 1);
  const pedRangeEnd = pedPageSize <= 0 ? pedTotal : Math.min(pedPage * pedPageSize, pedTotal);

  useEffect(() => { if (logPage > logTotalPages) setLogPage(1); }, [logPage, logTotalPages]);
  useEffect(() => { setLogPage(1); }, [trace, logSearch, logPageSize, opsFilter]);
  useEffect(() => { if (pedPage > pedTotalPages) setPedPage(1); }, [pedPage, pedTotalPages]);
  useEffect(() => { setPedPage(1); }, [pedidos, pedSearch, pedPageSize, pedStatus, opsFilter]);

  useEffect(() => {
    if (sideCollapsed) {
      setLogPageSize(10);
      setLogPage(1);
    }
  }, [sideCollapsed]);

  useEffect(() => {
    if (!opsOpen) return undefined;
    const onDown = (e) => {
      if (e.target.closest('.crm-rtz-ops-inline')) return;
      setOpsOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpsOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [opsOpen]);

  const toggleOps = useCallback(() => {
    setOpsOpen((prev) => {
      const next = !prev;
      if (next && opsBtnRef.current) {
        const r = opsBtnRef.current.getBoundingClientRect();
        setOpsPos({ top: r.bottom + 8, left: r.left });
      }
      return next;
    });
  }, []);

  const toggleLog = useCallback((id) => {
    setOpenLogId((prev) => (prev === id ? null : id));
  }, []);

  const kpiCards = useMemo(
    () => [
      { label: 'Creados', value: totals.pedidos_creados ?? 0, hint: 'Alta en el período', tone: 'create' },
      { label: 'Cambios', value: totals.cambios_estado ?? 0, hint: 'Eventos registrados', tone: 'assign' },
      { label: 'Entregas', value: totals.entregas ?? 0, hint: 'Marcadas en el período', tone: 'success' },
      { label: 'Cancelad.', value: totals.cancelaciones ?? 0, hint: 'En el período', tone: 'danger' },
      { label: 'En curso', value: totals.activos ?? 0, hint: 'Pedidos no finalizados', tone: 'emit' }
    ],
    [totals]
  );

  const openPedidoDetail = useCallback(async (pedido) => {
    if (!pedido?.id) return;
    setDetailPedido(pedido);
    setDetailItems([]);
    setDetailLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/asociacion-recetas/pedidos-callcenter/${pedido.id}/hist`,
        { credentials: 'include' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Error ${res.status}`);
      setDetailItems(Array.isArray(data.history) ? data.history : []);
    } catch (_) {
      setDetailItems([]);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const clearPedidoFilter = useCallback(() => {
    setPedidoId('');
    setDetailPedido(null);
  }, []);

  const selectedPedidoId = pedidoId.trim() ? Number(pedidoId.trim()) : null;

  const togglePedidoFilter = useCallback(
    (pedido) => {
      const id = Number(pedido?.id);
      if (!Number.isFinite(id) || id <= 0) return;
      if (pedidoId.trim() === String(id)) {
        clearPedidoFilter();
        return;
      }
      setPedidoId(String(id));
    },
    [pedidoId, clearPedidoFilter]
  );

  return (
    <div className="crm-pl-main">
      <div className="crm-gd-toolbar crm-pl-toolbar crm-rtz-toolbar">
        <div className="crm-pl-presets" role="group" aria-label="Período rápido">
          {PERIOD_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={`crm-pl-preset${activePreset === preset.id ? ' is-active' : ''}`}
              onClick={() => applyPreset(preset.days, preset.id)}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="crm-pl-filters">
          <label className="crm-pl-field crm-pl-field--date">
            <span>Desde</span>
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
                setActivePreset('');
              }}
            />
          </label>
          <label className="crm-pl-field crm-pl-field--date">
            <span>Hasta</span>
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => {
                setTo(e.target.value);
                setActivePreset('');
              }}
            />
          </label>
          <label className="crm-pl-field crm-pl-field--estado">
            <span>Estado</span>
            <select value={estado} onChange={(e) => setEstado(e.target.value)}>
              {ESTADO_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="crm-pl-field crm-pl-field--dni">
            <span>DNI</span>
            <input
              value={clienteDni}
              onChange={(e) => setClienteDni(e.target.value)}
              placeholder="12345678"
              inputMode="numeric"
            />
          </label>
        </div>

        <div className={`crm-rtz-kpis${loading ? ' is-loading' : ''}`}>
          {kpiCards.map((kpi) => (
            <div key={kpi.label} className={`crm-rtz-kpi is-${kpi.tone}`} title={kpi.hint}>
              <span className="crm-rtz-kpi__num">{kpi.value}</span>
              <span className="crm-rtz-kpi__label">{kpi.label}</span>
            </div>
          ))}
        </div>
      </div>

      {error ? (
        <div className="crm-pl-alert" role="alert">
          {error}
        </div>
      ) : null}

      <div className={`crm-pl-split${sideCollapsed ? ' crm-rtz-split--collapsed' : ''}`}>
        <aside className={`crm-pl-side-panel${sideCollapsed ? ' crm-rtz-side--collapsed' : ''}`}>
          {sideCollapsed ? (
            <button
              type="button"
              className="crm-rtz-side-rail"
              onClick={() => setSideCollapsed(false)}
              title="Expandir panel"
              aria-label="Expandir panel de pedidos"
            >
              <span className="crm-rtz-side-rail__chevron" aria-hidden="true">»</span>
              <span className="crm-rtz-side-rail__icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="3.5" width="16" height="17" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
                  <path d="M8 8.5h8M8 12h8M8 15.5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
              <span className="crm-rtz-side-rail__count">{pedidos.length}</span>
            </button>
          ) : (
            <div className="crm-pl-panel-card">
              <div className="crm-pl-panel-head crm-pl-panel-head--compact">
                <h2>Pedidos del período</h2>
                <div className="crm-rtz-side-head-right">
                  {!loading && operators.length > 0 ? (
                    <div className={`crm-rtz-ops-inline${opsOpen ? ' is-open' : ''}${opsFilter ? ' is-filtering' : ''}`}>
                      <button
                        ref={opsBtnRef}
                        type="button"
                        className="crm-rtz-ops-btn"
                        onClick={toggleOps}
                        title={opsFilter ? `Filtrando por ${opsFilter}` : 'Ver operadores'}
                        aria-label="Ver operadores"
                        aria-expanded={opsOpen}
                      >
                        <span className="crm-rtz-ops-btn__ic" aria-hidden="true">👥</span>
                        <span className="crm-rtz-ops-btn__count">{operators.length}</span>
                        {opsFilter ? <span className="crm-rtz-ops-btn__dot" aria-hidden="true" /> : null}
                      </button>
                      {opsOpen ? (
                        <div
                          className="crm-rtz-ops-pop"
                          role="dialog"
                          aria-label="Operadores"
                          style={{ top: opsPos.top, left: opsPos.left }}
                        >
                          <div className="crm-rtz-ops-pop__head">
                            <span className="crm-rtz-ops-pop__title">
                              <span aria-hidden="true">👥</span>
                              Operadores
                            </span>
                            <span className="crm-rtz-ops-pop__count">{operators.length}</span>
                            <button
                              type="button"
                              className="crm-rtz-ops-pop__close"
                              onClick={() => setOpsOpen(false)}
                              aria-label="Cerrar"
                            >
                              ✕
                            </button>
                          </div>
                          <p className="crm-rtz-ops-pop__hint">
                            {opsFilter ? 'Filtrando pedidos del operador' : 'Tocá un operador para filtrar'}
                          </p>
                          <div className="crm-rtz-ops-pop__list">
                            <button
                              type="button"
                              className={`crm-rtz-op crm-rtz-op--all${!opsFilter ? ' is-active' : ''}`}
                              onClick={() => setOpsFilter(null)}
                            >
                              <span className="crm-rtz-op__avatar crm-rtz-op__avatar--all" aria-hidden="true">★</span>
                              <div className="crm-rtz-op__info">
                                <strong className="crm-rtz-op__name">Todos los operadores</strong>
                                <span className="crm-rtz-op__stats">Sin filtro</span>
                              </div>
                              {!opsFilter ? <span className="crm-rtz-op__check" aria-hidden="true">✓</span> : null}
                            </button>
                            {operators.map((op) => {
                              const active = opsFilter === op.operator_name;
                              return (
                                <button
                                  key={op.operator_name}
                                  type="button"
                                  className={`crm-rtz-op crm-rtz-op--btn${active ? ' is-active' : ''}`}
                                  title={op.operator_name}
                                  onClick={() => setOpsFilter(active ? null : op.operator_name)}
                                  aria-pressed={active}
                                >
                                  <span className={`crm-rtz-op__rank${op.rank <= 3 ? ` is-top-${op.rank}` : ''}`}>
                                    {op.rank}
                                  </span>
                                  <span className="crm-rtz-op__avatar" aria-hidden="true">
                                    {operatorInitials(op.operator_name)}
                                  </span>
                                  <div className="crm-rtz-op__info">
                                    <strong className="crm-rtz-op__name">{op.operator_name}</strong>
                                    <span className="crm-rtz-op__stats">
                                      {op.cambios_count} cambios · {op.pedidos_count} ped · {op.entregas_count || 0} ent
                                    </span>
                                  </div>
                                  <span className="crm-rtz-op__check" aria-hidden="true">{active ? '✓' : ''}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <span className="crm-pl-badge">{pedidos.length}</span>
                  <button
                    type="button"
                    className="crm-rtz-side-collapse"
                    onClick={() => setSideCollapsed(true)}
                    title="Colapsar panel"
                    aria-label="Colapsar panel de pedidos"
                  >
                    «
                  </button>
                </div>
              </div>
              {!loading && pedidos.length > 0 ? (
                <div className="crm-rtz-sidetools">
                  <div className="crm-rtz-tablesearch">
                    <span className="crm-rtz-tablesearch__ic" aria-hidden="true">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                        <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="crm-rtz-tablesearch__input"
                      placeholder="Buscar pedido, paciente o DNI…"
                      value={pedSearch}
                      onChange={(e) => setPedSearch(e.target.value)}
                      aria-label="Buscar pedidos"
                    />
                    {pedSearch ? (
                      <button
                        type="button"
                        className="crm-rtz-tablesearch__clear"
                        onClick={() => setPedSearch('')}
                        aria-label="Limpiar búsqueda"
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                  <label className="crm-rtz-tablesize">
                    <span>Mostrar</span>
                    <div className="crm-rtz-tablesize__wrap">
                      <select
                        className="crm-rtz-tablesize__select"
                        value={pedPageSize <= 0 ? 'ALL' : String(pedPageSize)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setPedPageSize(v === 'ALL' ? 0 : Number(v));
                        }}
                        aria-label="Pedidos por página"
                      >
                        <option value="6">6</option>
                        <option value="12">12</option>
                        <option value="24">24</option>
                        <option value="ALL">Todos</option>
                      </select>
                      <span className="crm-rtz-tablesize__caret" aria-hidden="true">▾</span>
                    </div>
                  </label>
                </div>
              ) : null}
              {!loading && pedidos.length > 0 ? (
                <div className="crm-rtz-sidechips" role="group" aria-label="Filtrar por estado">
                  {[
                    { id: 'all', label: 'Todos', cls: 'all' },
                    { id: 'activo', label: 'En curso', cls: 'pendiente' },
                    { id: 'entregado', label: 'Entreg.', cls: 'aprobado' },
                    { id: 'cancelado', label: 'Cancel.', cls: 'rechazado' }
                  ].map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className={`crm-rtz-sidechip is-${chip.cls}${pedStatus === chip.id ? ' is-active' : ''}`}
                      onClick={() => setPedStatus(chip.id)}
                    >
                      {chip.label}
                      <span className="crm-rtz-sidechip__count">{pedStatusCounts[chip.id] ?? 0}</span>
                    </button>
                  ))}
                </div>
              ) : null}
              {!loading && selectedPedidoId ? (
                <button
                  type="button"
                  className="crm-pl-filter-chip crm-rtz-sidefilter"
                  onClick={clearPedidoFilter}
                  title="Quitar filtro y ver todos los pedidos"
                >
                  <span>Filtrando #{selectedPedidoId}</span>
                  <span className="crm-pl-filter-chip__x" aria-hidden>✕</span>
                </button>
              ) : null}
              <div className="crm-pl-side-body">
                {loading ? (
                  <LedgerLoading message="Cargando pedidos…" />
                ) : pedidos.length === 0 ? (
                  <p className="crm-pl-empty">Sin pedidos para el filtro actual.</p>
                ) : pedTotal === 0 ? (
                  <div className="crm-rtz-sidenoresults">
                    <span className="crm-rtz-sidenoresults__ic" aria-hidden="true">🔎</span>
                    <span>
                      {pedSearch.trim()
                        ? <>Sin coincidencias para “{pedSearch}”.</>
                        : 'Sin pedidos en este estado.'}
                    </span>
                    <button
                      type="button"
                      className="crm-rtz-sidenoresults__btn"
                      onClick={() => { setPedSearch(''); setPedStatus('all'); }}
                    >
                      Limpiar
                    </button>
                  </div>
                ) : (
                  <div className="crm-pl-pedidos-block">
                    {pedPageItems.map((p) => (
                      <div
                        key={p.id}
                        className={`crm-pl-pedido-item${selectedPedidoId === Number(p.id) ? ' is-selected' : ''}`}
                      >
                        <button
                          type="button"
                          className="crm-pl-pedido-item__main"
                          onClick={() => togglePedidoFilter(p)}
                          title={selectedPedidoId === Number(p.id) ? 'Quitar filtro' : 'Filtrar por este pedido'}
                        >
                          <div className="crm-pl-pedido-item__top">
                            <span className="crm-pl-pedido-item__id">#{p.id}</span>
                            <EstadoPill estado={p.estado} compact />
                          </div>
                          <div className="crm-pl-pedido-item__patient" title={p.paciente || ''}>
                            {p.paciente || 'Sin nombre'}
                          </div>
                          <div className="crm-pl-pedido-item__meta">
                            DNI {formatDni(p.cliente_dni)}
                            {p.nro_pedido_farmacia ? ` · ${p.nro_pedido_farmacia}` : ''}
                            {p.cambios_count ? ` · ${p.cambios_count} mov.` : ''}
                          </div>
                        </button>
                        <DetailEyeButton
                          pedidoId={p.id}
                          className="crm-pl-pedido-item__detail"
                          onClick={() => void openPedidoDetail(p)}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {!loading && pedTotal > 0 ? (
                <div className="crm-rtz-pager crm-rtz-pager--mini">
                  <div className="crm-rtz-pager__meta">
                    {pedRangeStart}–{pedRangeEnd} de {pedTotal}
                  </div>
                  {pedTotalPages > 1 ? (
                    <nav className="crm-rtz-pager__nav" aria-label="Paginación de pedidos">
                      <button
                        type="button"
                        className="crm-rtz-pager__btn"
                        onClick={() => setPedPage((p) => Math.max(1, p - 1))}
                        disabled={pedPage <= 1}
                        aria-label="Página anterior"
                      >
                        ‹
                      </button>
                      <span className="crm-rtz-pager__current">{pedPage}/{pedTotalPages}</span>
                      <button
                        type="button"
                        className="crm-rtz-pager__btn"
                        onClick={() => setPedPage((p) => Math.min(pedTotalPages, p + 1))}
                        disabled={pedPage >= pedTotalPages}
                        aria-label="Página siguiente"
                      >
                        ›
                      </button>
                    </nav>
                  ) : null}
                </div>
              ) : null}
            </div>
          )}
        </aside>

        <section className="crm-pl-table-panel">
          <div className="crm-pl-panel-card">
            <div className="crm-pl-panel-head">
              <div>
                <h2>Bitácora Operativa</h2>
              </div>
              <span className="crm-pl-badge">
                {groupedTrace.length} pedido{groupedTrace.length === 1 ? '' : 's'}
                {trace.length ? ` · ${trace.length} mov.` : ''}
              </span>
            </div>
            {!loading && trace.length > 0 ? (
              <div className="crm-rtz-tabletools">
                <div className="crm-rtz-tablesearch">
                  <span className="crm-rtz-tablesearch__ic" aria-hidden="true">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="crm-rtz-tablesearch__input"
                    placeholder="Buscar por pedido, paciente o DNI…"
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    aria-label="Buscar movimientos"
                  />
                  {logSearch ? (
                    <button
                      type="button"
                      className="crm-rtz-tablesearch__clear"
                      onClick={() => setLogSearch('')}
                      aria-label="Limpiar búsqueda"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
                <label className="crm-rtz-tablesize">
                  <span>Mostrar</span>
                  <div className="crm-rtz-tablesize__wrap">
                    <select
                      className="crm-rtz-tablesize__select"
                      value={logPageSize <= 0 ? 'ALL' : String(logPageSize)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setLogPageSize(v === 'ALL' ? 0 : Number(v));
                      }}
                      aria-label="Pedidos por página"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                      <option value="ALL">Todos</option>
                    </select>
                    <span className="crm-rtz-tablesize__caret" aria-hidden="true">▾</span>
                  </div>
                </label>
              </div>
            ) : null}
            <div className="crm-pl-table-scroll">
              {loading ? (
                <LedgerLoading message="Cargando eventos…" />
              ) : trace.length === 0 ? (
                <p className="crm-pl-empty">No hay eventos para el filtro actual.</p>
              ) : logTotal === 0 ? (
                <div className="crm-rtz-noresults">
                  <span className="crm-rtz-noresults__ic" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                      <path d="M20 20l-3.2-3.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </span>
                  <div className="crm-rtz-noresults__title">Sin coincidencias</div>
                  <div className="crm-rtz-noresults__text">
                    No encontramos pedidos para “{logSearch}”.
                  </div>
                  <button
                    type="button"
                    className="crm-rtz-noresults__btn"
                    onClick={() => setLogSearch('')}
                  >
                    Limpiar búsqueda
                  </button>
                </div>
              ) : (
                <div className={`crm-rtz-log${sideCollapsed ? ' crm-rtz-log--grid' : ''}`}>
                  {logPageItems.map((group) => {
                    const isOpen = openLogId === group.pedidoId;
                    return (
                      <article
                        className={`crm-rtz-log-card${isOpen ? ' is-open' : ''}`}
                        key={`pl-group-${group.pedidoId}`}
                      >
                        <div
                          className="crm-rtz-log-card__head"
                          role="button"
                          tabIndex={0}
                          aria-expanded={isOpen}
                          onClick={() => toggleLog(group.pedidoId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleLog(group.pedidoId);
                            }
                          }}
                        >
                          <span className="crm-rtz-log-card__chevron" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </span>
                          <div className="crm-rtz-log-card__idline">
                            <span className="crm-rtz-log-card__id">#{group.pedidoId}</span>
                            {group.nro_pedido_farmacia ? (
                              <span className="crm-pl-table__ref">{group.nro_pedido_farmacia}</span>
                            ) : null}
                            {group.estadoActual ? <EstadoPill estado={group.estadoActual} compact /> : null}
                            <span className="crm-rtz-log-card__count">{group.events.length} mov.</span>
                          </div>
                          <div className="crm-rtz-log-card__patient">
                            <span className="crm-rtz-log-card__name" title={group.paciente || ''}>
                              {group.paciente || 'Sin nombre'}
                            </span>
                            <span className="crm-rtz-log-card__dni">DNI {formatDni(group.cliente_dni)}</span>
                          </div>
                          <DetailEyeButton
                            pedidoId={group.pedidoId}
                            className="crm-rtz-log-card__eye"
                            onClick={(e) => {
                              e.stopPropagation();
                              void openPedidoDetail({
                                id: group.pedidoId,
                                paciente: group.paciente,
                                nro_pedido_farmacia: group.nro_pedido_farmacia
                              });
                            }}
                          />
                        </div>
                        <div className="crm-rtz-log-card__body">
                          <div className="crm-rtz-log-card__body-inner">
                            <ol className="crm-rtz-log-steps">
                              {group.events.map((row) => {
                                const tone = plEventTone(row.new_estado);
                                return (
                                  <li key={row.id} className={`crm-rtz-log-step is-${tone}`}>
                                    <span className="crm-rtz-log-step__marker" aria-hidden="true">
                                      {plEventIcon(row.new_estado)}
                                    </span>
                                    <div className="crm-rtz-log-step__content">
                                      <div className="crm-rtz-log-step__row">
                                        <span className="crm-rtz-log-step__title">
                                          {estadoLabel(row.new_estado)}
                                        </span>
                                        <span className="crm-rtz-log-step__time">
                                          {formatDateTimeCompact(row.created_at)}
                                        </span>
                                      </div>
                                      <div className="crm-rtz-log-step__targets">
                                        <EstadoTransition
                                          previousEstado={row.previous_estado}
                                          newEstado={row.new_estado}
                                        />
                                      </div>
                                      {row.notas_cambio ? (
                                        <div className="crm-rtz-log-step__note">{row.notas_cambio}</div>
                                      ) : null}
                                      <div className="crm-rtz-log-step__actor">
                                        <span className="crm-rtz-log-step__avatar" aria-hidden="true">
                                          {operatorInitials(row.changed_by || 'Sistema')}
                                        </span>
                                        <span className="crm-rtz-log-step__actor-name">
                                          {row.changed_by || 'Sistema'}
                                        </span>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ol>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
            {!loading && logTotal > 0 ? (
              <div className="crm-rtz-pager">
                <div className="crm-rtz-pager__meta">
                  Mostrando <strong>{logRangeStart}</strong>–<strong>{logRangeEnd}</strong> de <strong>{logTotal}</strong> pedido{logTotal === 1 ? '' : 's'}
                </div>
                {logTotalPages > 1 ? (
                  <nav className="crm-rtz-pager__nav" aria-label="Paginación de movimientos">
                    <button
                      type="button"
                      className="crm-rtz-pager__btn"
                      onClick={() => setLogPage(1)}
                      disabled={logPage <= 1}
                      aria-label="Primera página"
                    >
                      «
                    </button>
                    <button
                      type="button"
                      className="crm-rtz-pager__btn"
                      onClick={() => setLogPage((p) => Math.max(1, p - 1))}
                      disabled={logPage <= 1}
                      aria-label="Página anterior"
                    >
                      ‹
                    </button>
                    <span className="crm-rtz-pager__current">
                      Página <strong>{logPage}</strong> de {logTotalPages}
                    </span>
                    <button
                      type="button"
                      className="crm-rtz-pager__btn"
                      onClick={() => setLogPage((p) => Math.min(logTotalPages, p + 1))}
                      disabled={logPage >= logTotalPages}
                      aria-label="Página siguiente"
                    >
                      ›
                    </button>
                    <button
                      type="button"
                      className="crm-rtz-pager__btn"
                      onClick={() => setLogPage(logTotalPages)}
                      disabled={logPage >= logTotalPages}
                      aria-label="Última página"
                    >
                      »
                    </button>
                  </nav>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      </div>

      {detailPedido ? (
        <div className="crm-cw-forward-overlay" onClick={() => setDetailPedido(null)}>
          <div
            className="crm-farm-modal-box crm-farm-traza-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Registro del pedido ${detailPedido.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="crm-cw-forward-head">
              <strong>Registro #{detailPedido.id}</strong>
            </div>
            <div className="crm-farm-modal-body">
              <div className="crm-farm-notas-modal__patient">
                {detailPedido.paciente || 'Paciente'}
              </div>
              {detailPedido.nro_pedido_farmacia ? (
                <div className="crm-farm-notas-modal__ref" style={{ marginBottom: 12 }}>
                  N° {detailPedido.nro_pedido_farmacia}
                </div>
              ) : null}
              {detailLoading ? (
                <div className="crm-farm-traza-loading">Cargando registro…</div>
              ) : detailItems.length === 0 ? (
                <div className="crm-farm-traza-empty">Sin movimientos registrados todavía.</div>
              ) : (
                <ol className="crm-farm-traza-timeline">
                  {detailItems.map((item, idx) => (
                    <li key={item.id || `hist-${idx}`} className="crm-farm-traza-item">
                      <div className="crm-farm-traza-item__dot" aria-hidden />
                      <div className="crm-farm-traza-item__body">
                        <div className="crm-farm-traza-item__estados">
                          {item.previous_estado ? (
                            <span className="crm-farm-traza-item__from">
                              {estadoLabel(item.previous_estado)}
                            </span>
                          ) : (
                            <span className="crm-farm-traza-item__from">Inicio</span>
                          )}
                          <span className="crm-farm-traza-item__arrow" aria-hidden>
                            →
                          </span>
                          <span className="crm-farm-traza-item__to">
                            {estadoLabel(item.new_estado)}
                          </span>
                        </div>
                        <div className="crm-farm-traza-item__meta">
                          <span>{formatDateTime(item.created_at)}</span>
                          {item.changed_by ? <span>· {item.changed_by}</span> : null}
                        </div>
                        {item.notas_cambio ? (
                          <div className="crm-farm-traza-item__note">{item.notas_cambio}</div>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              <div className="crm-farm-modal-actions">
                <button
                  type="button"
                  className="crm-gd-btn crm-gd-btn--primary"
                  onClick={() => setDetailPedido(null)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
